import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import ProjectInfoValues from '../ProjectInfoValues';
import SelectBox from '../../../shared/component/SelectBox';
import MoleculeInputDiv from '../../../shared/component/molecule/MoleculeInputDiv';
import { PIInput } from '../../../shared/component/headless/input/InputBody';
import UniversalIconBtn from '../../../shared/component/headless/button/UniversalIconBtn';
import HeadlessBtn from '../../../shared/component/headless/button/HeadlessBtn';
import { BlackFullWidthBtn } from '../../../shared/component/headless/button/BtnBody';
import { MobileHeader as HeaderRow, Title } from '../../../shared/component/modal/SearchModalLayout';
import { getBtnName } from '../../../shared/constants/btnNames';
import { getHintMessage } from '../../../shared/constants/hintMessages';
import { PROJECT_INFO_NAME_MAX_LENGTH } from '../../../shared/constants/limits';
import { ProjectInfoData } from '../../../shared/dto/EntityRepository';
import { ProjectInfoType, SelectType } from '../../../shared/enum/EnumRepository';
import { useEditMode } from '../../../shared/hooks/useEditMode';
import { useModalStore } from '../../../shared/store/portal/modalStore';
import { useProjectInfoListStore } from '../../../shared/store/projectInfoStore';
import { getProjectInfoNamePlaceholder, getProjectInfoTypeLabel } from '../../../shared/util/getProjectInfoTypeLabel';
import {
  getProjectInfoDisplayName,
  isSameProjectInfo,
  normalizeProjectInfoType,
  withProjectInfoType,
  withoutVenueIds,
} from '../../../shared/util/projectInfo';
import belowIcon from '../../../asset/icon/below.png';

const cloneProjectInfo = (data: ProjectInfoData): ProjectInfoData => ({
  ...data,
  customValues: data.customValues.map((value) => ({ ...value })),
});

const ProjectInfoEditModalMobile: React.FC = () => {
  const { standardModal, clearModal } = useModalStore();
  const { isEditMode } = useEditMode();
  const data = useProjectInfoListStore((s) => s.projectInfoList.find((info) => info.id === standardModal?.value));
  const updateProjectInfo = useProjectInfoListStore((s) => s.updateProjectInfo);
  const [draft, setDraft] = useState<ProjectInfoData | null>(() => data ? cloneProjectInfo(data) : null);

  useEffect(() => {
    if (!data || !draft || !isEditMode) clearModal();
  }, [data, draft, isEditMode, clearModal]);

  if (!data || !draft || !isEditMode) return null;
  const projectInfoType = normalizeProjectInfoType(draft.type);

  const handleDraftChange = (updates: Partial<ProjectInfoData>) => {
    setDraft((current) => {
      if (!current) return current;
      const nextType = normalizeProjectInfoType(updates.type ?? current.type);
      const nextValues = updates.customValues ?? current.customValues;
      return {
        ...current,
        ...updates,
        type: nextType,
        customValues: nextType === ProjectInfoType.LOCATION
          ? nextValues
          : withoutVenueIds(nextValues),
      };
    });
  };

  const handleTypeChange = (type: ProjectInfoType) => {
    const nextType = normalizeProjectInfoType(type);
    if (nextType === projectInfoType) return;
    handleDraftChange(withProjectInfoType(nextType));
  };

  const handleApply = () => {
    if (!isSameProjectInfo(data, draft)) {
      updateProjectInfo(data.id, {
        type: draft.type,
        customName: draft.customName,
        customValues: draft.customValues.map((value) => ({ ...value })),
      });
    }
    clearModal();
  };

  return (
    <Container>
      <HeaderRow>
        <Title>{getHintMessage('EDIT_INFO')}</Title>
        <UniversalIconBtn
          size="fit"
          border={false}
          isTransparent
          backgroundImg={belowIcon}
          onClick={() => clearModal()}
          aria-label="Close"
        />
      </HeaderRow>
      <Editor>
        <SelectBoxWrapper>
          <SelectBox
            value={projectInfoType}
            selectType={SelectType.PROJECT_INFO_TYPE}
            handleChange={handleTypeChange}
            direction={false}
            getDisplayText={getProjectInfoTypeLabel}
          />
        </SelectBoxWrapper>
        <MoleculeInputDiv
          value={getProjectInfoDisplayName(draft.customName)}
          placeholder={getProjectInfoNamePlaceholder(projectInfoType)}
          handleChange={(event) => handleDraftChange({ customName: event.target.value })}
          inputStyle={PIInput}
          StyledDiv={NameSection}
          maxLength={PROJECT_INFO_NAME_MAX_LENGTH}
        />
        <ProjectInfoValues
          data={draft}
          onChange={handleDraftChange}
          canManageLinks={false}
          canReorderValues
        />
      </Editor>
      <HeadlessBtn
        value={getBtnName('APPLY')}
        handleClick={handleApply}
        StyledBtn={SubmitButton}
      />
    </Container>
  );
};

export default ProjectInfoEditModalMobile;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
`;

const Editor = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 60vh;
  overflow-y: auto;
  padding-bottom: 20px;
`;

const SelectBoxWrapper = styled.div`
  width: 160px;
  max-width: 100%;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

const NameSection = styled.div`
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_03_2};
`;

const SubmitButton = styled(BlackFullWidthBtn)`
  margin: 20px 0px 15px 0px;
`;
